import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { useMount } from 'ahooks';
import {
  Table,
  Button,
  Modal,
  Input,
  Select,
  notification,
  Justify,
  Layout,
} from 'tea-component';
import type { SelectOption, TableColumn } from 'tea-component';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import useTableAutotipAddon from '@home/components/TeaComponents/Table/_hooks/useTableAutotipAddon';
import SvgIcon from '@home/components/SvgIcon';
import {
  fetchPersonalTokens,
  createPersonalToken,
  fetchPersonalTokenDetail,
  renewPersonalToken,
  revokePersonalToken,
  PERSONAL_TOKEN_DEFAULT_EXPIRE_MONTHS,
} from '@user-center/services/personalToken';
import type { TPersonalTokenType } from '@user-center/services/personalToken';

import './index.scss';

const { Body, Content } = Layout;

type IProps = IBaseProps;

interface ITokenRow {
  id: number;
  token_name: string;
  token_type?: TPersonalTokenType;
  masked_token: string;
  expire_time: string;
  created_time: string;
}

interface ITokenDetail {
  id: number;
  token_name: string;
  token: string;
  token_type?: TPersonalTokenType;
  expire_time: string;
}

const formatDateTime = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => `${n}`.padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const maskTokenTail6 = (token?: string): string => {
  const raw = (token || '').trim();
  if (!raw) return '-';
  const tail6 = raw.slice(-6);
  return `******${tail6}`;
};

const toValidId = (value: unknown): number | null => {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
};

const PERSONAL_TOKEN_ERROR_CODE_MAP: Record<number, string> = {
  90101: 'personal_token_name_invalid',
  90102: 'personal_token_not_found',
  90103: 'personal_token_already_revoked',
  90104: 'personal_token_create_failed',
  90105: 'personal_token_service_error',
};

type TFormatMessage = (descriptor: { id: string; }) => string;

const getPersonalTokenErrorMessage = (
  error: any,
  formatMessage: TFormatMessage,
  fallbackId: string,
): string => {
  const key = PERSONAL_TOKEN_ERROR_CODE_MAP[Number(error?.code)];
  if (key) {
    return formatMessage({ id: key });
  }
  return error?.msg || formatMessage({ id: fallbackId });
};

const validateCreateName = (
  value: string,
  formatMessage: TFormatMessage,
): string => {
  const text = (value || '').trim();
  if (!text) return formatMessage({ id: 'personal_token_name_required' });
  if (text.length > 50) {
    return formatMessage({ id: 'personal_token_name_too_long' });
  }
  if (!/^[\u4e00-\u9fa5A-Za-z0-9_]+$/.test(text)) {
    return formatMessage({ id: 'personal_token_name_invalid' });
  }
  return '';
};

const DEFAULT_CREATE_TOKEN_TYPE: TPersonalTokenType = 'personal_token';

const normalizeTokenType = (value: unknown): TPersonalTokenType | undefined => {
  if (value === 'personal_token' || value === 'game_wiki') {
    return value;
  }
  return undefined;
};

const getTokenValidityMessageId = (
  tokenType?: TPersonalTokenType,
): string =>
  tokenType === 'game_wiki'
    ? 'personal_token_valid_12_months'
    : 'personal_token_valid_90_days';

const getTokenRenewDescriptionId = (
  tokenType?: TPersonalTokenType,
): string =>
  tokenType === 'game_wiki'
    ? 'personal_token_renew_confirm_desc_game_wiki'
    : 'personal_token_renew_confirm_desc';

const runAsync = (callback: () => Promise<void>): void => {
  void callback();
};

const iconList: any[] = [];

const PersonalTokensCenter: React.FC<IProps> = (props) => {
  const { style, className } = props;
  const { formatMessage } = useIntl();

  const [loading, setLoading] = useState<boolean>(false);
  const [tokens, setTokens] = useState<ITokenRow[]>([]);

  const [createVisible, setCreateVisible] = useState<boolean>(false);
  const [createName, setCreateName] = useState<string>('');
  const [createNameError, setCreateNameError] = useState<string>('');
  const [createType, setCreateType] = useState<TPersonalTokenType>(
    DEFAULT_CREATE_TOKEN_TYPE,
  );
  const [creating, setCreating] = useState<boolean>(false);

  const [detailVisible, setDetailVisible] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<ITokenDetail | null>(null);
  const [showToken, setShowToken] = useState<boolean>(false);

  const [revokeVisible, setRevokeVisible] = useState<boolean>(false);
  const [targetToken, setTargetToken] = useState<ITokenRow | null>(null);
  const [revoking, setRevoking] = useState<boolean>(false);

  const fetchList = async (): Promise<void> => {
    try {
      setLoading(true);
      const res = await fetchPersonalTokens();
      const list = (res?.data?.list || []).reduce<ITokenRow[]>((acc, item) => {
        const id = toValidId(item?.id);
        if (id === null) {
          return acc;
        }
        const tokenType = normalizeTokenType(item?.token_type);
        acc.push({
          id,
          token_name: item.token_name || '',
          token_type: tokenType,
          masked_token: maskTokenTail6(item.masked_token || ''),
          expire_time: item.expire_time || '',
          created_time: item.created_time || '',
        });
        return acc;
      }, []);

      const sortedList = [...list].sort((a, b) => {
        return (
          new Date(b.created_time || '').getTime() -
          new Date(a.created_time || '').getTime()
        );
      });
      setTokens(sortedList);
    } catch (error: any) {
      notification.error({
        unique: true,
        description: getPersonalTokenErrorMessage(
          error,
          formatMessage,
          'personal_token_list_failed',
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  useMount(() => {
    fetchList();
  });

  const autotipAddon = useTableAutotipAddon({
    isLoading: loading,
    loadingText: formatMessage({ id: 'db_loading' }),
    emptyText: formatMessage({ id: 'dbi_search_empty' }),
  });

  // 当前列表是否已存在 game_wiki 类型的令牌（用于置灰 + hover 提示）
  const hasGameWiki = useMemo<boolean>(
    () => tokens.some((t) => t.token_type === 'game_wiki'),
    [tokens],
  );

  // 创建弹窗：令牌类型下拉选项（game_wiki 在已存在时置灰，置灰项 hover 提示）
  const createTypeOptions = useMemo<SelectOption[]>(() => {
    const gameWikiTip = formatMessage({
      id: 'personal_token_type_game_wiki_exists_tip',
    });
    return [
      {
        value: 'personal_token',
        text: formatMessage({ id: 'personal_token_type_personal_token' }),
      },
      {
        value: 'game_wiki',
        text: formatMessage({ id: 'personal_token_type_game_wiki' }),
        disabled: hasGameWiki,
        tooltip: hasGameWiki ? gameWikiTip : undefined,
      },
    ];
  }, [formatMessage, hasGameWiki]);

  // 当前类型对应的默认时效（月），用于前端自动填写
  const createExpireMonths = useMemo<number>(
    () => PERSONAL_TOKEN_DEFAULT_EXPIRE_MONTHS[createType],
    [createType],
  );

  const columns = useMemo<TableColumn<ITokenRow>[]>(
    () => [
      {
        key: 'token_name',
        header: formatMessage({ id: 'personal_token_name' }),
        width: 260,
        render: (record: ITokenRow) => <span>{record.token_name || '-'}</span>,
      },
      {
        key: 'masked_token',
        header: formatMessage({ id: 'personal_token_value' }),
        width: 220,
        render: (record: ITokenRow) => (
          <span className="dbu-token-masked-box">
            {record.masked_token || '-'}
          </span>
        ),
      },
      {
        key: 'expire_time',
        header: formatMessage({ id: 'personal_token_expire_time' }),
        width: 150,
        align: 'left',
        render: (record: ITokenRow) => (
          <span>{formatDateTime(record.expire_time)}</span>
        ),
      },
      {
        key: 'actions',
        header: formatMessage({ id: 'dbi_actions' }),
        width: 260,
        render: (record: ITokenRow) => (
          <div className="dbu-token-actions">
            <Button
              type="link"
              className="dbu-token-action-btn dbu-token-action-btn--blue"
              onClick={(e) => {
                runAsync(async () => {
                  e?.stopPropagation();
                  try {
                    setShowToken(false);
                    setDetail(null);
                    setDetailLoading(true);
                    setDetailVisible(true);

                    const res = await fetchPersonalTokenDetail(record.id);
                    const data = res?.data;

                    setDetail({
                      id: toValidId(data?.id) ?? record.id,
                      token_name: data?.token_name || '',
                      token: data?.token || '',
                      token_type: normalizeTokenType(data?.token_type) ?? record.token_type,
                      expire_time: data?.expire_time || '',
                    });
                  } catch (error: any) {
                    setDetail(null);
                    setDetailVisible(false);
                    notification.error({
                      unique: true,
                      description: getPersonalTokenErrorMessage(
                        error,
                        formatMessage,
                        'personal_token_detail_failed',
                      ),
                    });
                  } finally {
                    setDetailLoading(false);
                  }
                });
              }}
            >
              {formatMessage({ id: 'personal_token_view' })}
            </Button>

            <Button
              type="link"
              className="dbu-token-action-btn dbu-token-action-btn--blue"
              onClick={(e) => {
                e?.stopPropagation();
                Modal.confirm({
                  message: formatMessage({
                    id: 'personal_token_renew_confirm_title',
                  }),
                  description: formatMessage({
                    id: getTokenRenewDescriptionId(record.token_type),
                  }),
                  okText: formatMessage({ id: 'dbh_confirm' }),
                  cancelText: formatMessage({ id: 'dbh_cancel' }),
                  onOk: async () => {
                    try {
                      await renewPersonalToken(record.id);
                      notification.success({
                        unique: true,
                        description: formatMessage({
                          id: 'personal_token_renew_success',
                        }),
                      });
                      await fetchList();
                    } catch (error: any) {
                      notification.error({
                        unique: true,
                        description: getPersonalTokenErrorMessage(
                          error,
                          formatMessage,
                          'personal_token_renew_failed',
                        ),
                      });
                    }
                  },
                });
              }}
            >
              {formatMessage({ id: 'personal_token_renew' })}
            </Button>

            <Button
              type="link"
              className="dbu-token-action-btn dbu-token-action-btn--danger"
              onClick={(e) => {
                e?.stopPropagation();
                setTargetToken(record);
                setRevokeVisible(true);
              }}
            >
              {formatMessage({ id: 'personal_token_revoke' })}
            </Button>
          </div>
        ),
      },
    ],
    [formatMessage],
  );

  return (
    <Layout style={style} className={classnames('dbu-personal-token-layout', className)}>
      <Body>
        <Content>
          <Content.Header
            title={formatMessage({ id: 'personal_token_page_title' })}
          />
          <Content.Body full>
            <div className="dbu-personal-token-page">
              <Justify
                className="dbu-personal-token-page__toolbar"
                left={
                  <Button
                    type="primary"
                    onClick={() => {
                      setCreateName('');
                      setCreateNameError('');
                      setCreateType(DEFAULT_CREATE_TOKEN_TYPE);
                      setCreateVisible(true);
                    }}
                  >
                    {formatMessage({ id: 'personal_token_create' })}
                  </Button>
                }
              />

              <CardSlotV2 iconList={iconList}>
                <Table
                  className="dbu-personal-token-page__table"
                  recordKey={(record: ITokenRow) => String(record.id)}
                  columns={columns}
                  records={loading ? [] : tokens}
                  addons={[autotipAddon]}
                />
              </CardSlotV2>

              <Modal
                className="dbu-personal-token-create-modal"
                visible={createVisible}
                onClose={() => {
                  setCreateVisible(false);
                  setCreateType(DEFAULT_CREATE_TOKEN_TYPE);
                }}
                caption={formatMessage({
                  id: 'personal_token_create_modal_title',
                })}
                size="l"
              >
                <Modal.Body>
                  <div className="dbu-ptm-create-row">
                    <div className="dbu-ptm-create-label">
                      <span className="dbu-ptm-required">*</span>
                      {formatMessage({ id: 'personal_token_name' })}
                    </div>
                    <div className="dbu-ptm-create-control">
                      <Input
                        className={createNameError ? 'dbu-ptm-input-error' : ''}
                        value={createName}
                        maxLength={50}
                        placeholder={formatMessage({
                          id: 'personal_token_name_placeholder',
                        })}
                        onChange={(value) => {
                          setCreateName(value);
                          if (createNameError) setCreateNameError('');
                        }}
                      />

                      <div
                        className={classnames('dbu-ptm-error-text', {
                          'is-visible': !!createNameError,
                        })}
                      >
                        {createNameError}
                      </div>
                    </div>
                  </div>

                  <div className="dbu-ptm-create-row">
                    <div className="dbu-ptm-create-label">
                      <span className="dbu-ptm-required">*</span>
                      {formatMessage({ id: 'personal_token_type' })}
                    </div>
                    <div className="dbu-ptm-create-control">
                      <Select
                        className="dbu-ptm-type-select"
                        appearance="button"
                        size="full"
                        options={createTypeOptions}
                        value={createType}
                        onChange={(value: string) => {
                          if (value === 'personal_token' || value === 'game_wiki') {
                            setCreateType(value);
                          }
                        }}
                        matchButtonWidth
                      />
                    </div>
                  </div>

                  {/* 时效按 token_type 自动计算并随请求提交，前端不展示 */}
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    onClick={() => {
                      setCreateVisible(false);
                      setCreateType(DEFAULT_CREATE_TOKEN_TYPE);
                    }}
                  >
                    {formatMessage({ id: 'dbh_cancel' })}
                  </Button>
                  <Button
                    type="primary"
                    loading={creating}
                    onClick={() => {
                      runAsync(async () => {
                        if (creating) return;
                        const errMsg = validateCreateName(createName, formatMessage);
                        if (errMsg) {
                          setCreateNameError(errMsg);
                          return;
                        }
                        // 防御：若选择 game_wiki 但列表中已存在，禁止提交
                        if (createType === 'game_wiki' && hasGameWiki) {
                          notification.error({
                            unique: true,
                            description: formatMessage({
                              id: 'personal_token_type_game_wiki_exists_tip',
                            }),
                          });
                          return;
                        }

                        try {
                          setCreating(true);
                          const res = await createPersonalToken({
                            token_name: createName.trim(),
                            token_type: createType,
                            expire_type: createExpireMonths,
                          });
                          const data = res?.data;

                          notification.success({
                            unique: true,
                            description: formatMessage({
                              id: 'personal_token_create_success',
                            }),
                          });

                          const createTokenName = createName.trim();
                          const createTokenType = createType;
                          const createTokenValue = data?.token || '';

                          setCreateVisible(false);
                          setCreateName('');
                          setCreateType(DEFAULT_CREATE_TOKEN_TYPE);
                          await fetchList();

                          // 创建接口返回完整 token 时仅在本次详情中展示；列表仍使用后6位掩膜
                          setDetail({
                            id: toValidId(data?.id) ?? 0,
                            token_name: data?.token_name || createTokenName,
                            token: createTokenValue,
                            token_type:
                              normalizeTokenType(data?.token_type) ?? createTokenType,
                            expire_time: data?.expire_time || '',
                          });
                          setShowToken(false);
                          setDetailVisible(true);
                        } catch (error: any) {
                          notification.error({
                            unique: true,
                            description: getPersonalTokenErrorMessage(
                              error,
                              formatMessage,
                              'personal_token_create_failed',
                            ),
                          });
                        } finally {
                          setCreating(false);
                        }
                      });
                    }}
                  >
                    {formatMessage({ id: 'dbh_confirm' })}
                  </Button>
                </Modal.Footer>
              </Modal>

              <Modal
                className="dbu-personal-token-detail-modal"
                visible={detailVisible}
                onClose={() => {
                  setDetail(null);
                  setDetailVisible(false);
                  setShowToken(false);
                }}
                caption={formatMessage({ id: 'personal_token_detail_modal_title' })}
                size="l"
              >
                <Modal.Body>
                  {detailLoading ? (
                    <div className="dbu-token-detail-loading">
                      {formatMessage({ id: 'db_loading' })}
                    </div>
                  ) : (
                    <>
                      <div className="dbu-token-warning">
                        {formatMessage({ id: 'personal_token_security_warning' })}
                      </div>

                      <div className="dbu-token-detail-field">
                        <div className="dbu-token-detail-field__label">
                          {formatMessage({ id: 'personal_token_name' })}：
                        </div>
                        <div className="dbu-token-detail-field__value">
                          {detail?.token_name || '-'}
                        </div>
                      </div>

                      <div className="dbu-token-detail-field">
                        <div className="dbu-token-detail-field__label">
                          {formatMessage({ id: 'personal_token_expire_time' })}：
                        </div>
                        <div className="dbu-token-detail-field__value">
                          {formatDateTime(detail?.expire_time)}
                          <span className="dbu-token-detail-hint">
                            （
                            {formatMessage({
                              id: getTokenValidityMessageId(detail?.token_type),
                            })}
                            ）
                          </span>
                        </div>
                      </div>

                      <div className="dbu-token-detail-field dbu-token-detail-field--token">
                        <div className="dbu-token-detail-field__label">
                          {formatMessage({ id: 'personal_token_value' })}：
                        </div>
                        <div className="dbu-token-detail-token-box">
                          <span className="dbu-token-detail-token-text">
                            {showToken
                              ? detail?.token || '-'
                              : maskTokenTail6(detail?.token)}
                          </span>
                          <button
                            type="button"
                            className="dbu-token-detail-eye-btn"
                            onClick={() => setShowToken((prev) => !prev)}
                            title={
                              showToken
                                ? formatMessage({ id: 'personal_token_hide' })
                                : formatMessage({ id: 'personal_token_show' })
                            }
                          >
                            <SvgIcon type={showToken ? 'open-eyes' : 'eyes-closed'} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    type="primary"
                    className="dbu-token-detail-copy-btn"
                    onClick={() => {
                      runAsync(async () => {
                        try {
                          const value = detail?.token || '';
                          if (!value) return;
                          await navigator.clipboard.writeText(value);
                          notification.success({
                            unique: true,
                            description: formatMessage({ id: 'copied_successfully' }),
                          });
                        } catch (error) {
                          notification.error({
                            unique: true,
                            description: formatMessage({ id: 'copy_failed' }),
                          });
                        }
                      });
                    }}
                  >
                    {formatMessage({ id: 'personal_token_copy' })}
                  </Button>
                </Modal.Footer>
              </Modal>

              <Modal
                className="dbu-personal-token-revoke-modal"
                visible={revokeVisible}
                onClose={() => {
                  setRevokeVisible(false);
                  setTargetToken(null);
                }}
                caption=""
                size="l"
              >
                <Modal.Body>
                  <div className="dbu-token-revoke-content">
                    <div className="dbu-token-revoke-header">
                      <span className="dbu-token-revoke-header__icon">!</span>
                      <span className="dbu-token-revoke-header__title">
                        {formatMessage({ id: 'personal_token_revoke_modal_title' })}
                      </span>
                    </div>

                    <div className="dbu-token-revoke-desc">
                      {formatMessage({ id: 'personal_token_revoke_warning' })}
                    </div>

                    <div className="dbu-token-revoke-item">
                      <span className="dbu-token-revoke-item__label">
                        {formatMessage({ id: 'personal_token_name' })}:
                      </span>
                      <span className="dbu-token-revoke-item__value">
                        {targetToken?.token_name || '-'}
                      </span>
                    </div>

                    <div className="dbu-token-revoke-item">
                      <span className="dbu-token-revoke-item__label">
                        {formatMessage({ id: 'personal_token_expire_time' })}:
                      </span>
                      <span className="dbu-token-revoke-item__value">
                        {formatDateTime(targetToken?.expire_time)}
                        <span className="dbu-token-revoke-item__hint">
                          (
                          {formatMessage({
                            id: getTokenValidityMessageId(targetToken?.token_type),
                          })}
                          )
                        </span>
                      </span>
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    className="dbu-token-revoke-cancel-btn"
                    onClick={() => {
                      setRevokeVisible(false);
                      setTargetToken(null);
                    }}
                  >
                    {formatMessage({ id: 'dbh_cancel' })}
                  </Button>
                  <Button
                    type="error"
                    className="dbu-token-revoke-confirm-btn"
                    loading={revoking}
                    onClick={() => {
                      runAsync(async () => {
                        try {
                          const tokenId = toValidId(targetToken?.id);
                          if (tokenId === null) return;
                          setRevoking(true);
                          await revokePersonalToken(tokenId);
                          notification.success({
                            unique: true,
                            description: formatMessage({
                              id: 'personal_token_revoke_success',
                            }),
                          });
                          setRevokeVisible(false);
                          setTargetToken(null);
                          await fetchList();
                        } catch (error: any) {
                          notification.error({
                            unique: true,
                            description: getPersonalTokenErrorMessage(
                              error,
                              formatMessage,
                              'personal_token_revoke_failed',
                            ),
                          });
                        } finally {
                          setRevoking(false);
                        }
                      });
                    }}
                  >
                    {formatMessage({ id: 'personal_token_revoke_confirm' })}
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
          </Content.Body>
        </Content>
      </Body>
    </Layout>
  );
};

export default PersonalTokensCenter;